import { consoleError } from '@/modules/logging/utils'
import { useCasterStreamInfo } from '@/modules/usermedia/compositions'
import store from '@/store'
import { computed, onMounted, onUnmounted, Ref, watch } from '@vue/composition-api'
import useTalkback from './talkback'
import { constructCastTalkbackTarget } from '@/helpers/talkbackHelpers'
import { Modals } from '@/modules/ui/types'
import RefCounter from '@/modules/common/classes/RefCounter'

// This composition attempts to ensure that current user stream is added to target cast for talkback
// only once, even if there are multiple components using this composition to do talkback with that cast
// When the last component is unmounted, current user stream will be removed from target cast.
// But if a component C does not use this composition to manage talkback with target cast,
// there is a possibility that unmounting of the last component using this composition would remove
// the current user's stream from the target cast causing the component C to lose its connection to cast

const castClientCount = new RefCounter('cast')

const useCastTalkback = (castId: Ref<string|null>) => {

  const cast = computed(() => (castId.value ? store.state.casts.subscribedCasts[castId.value] : null) ?? null)
  const isUserMediaConnected = computed(() => store.state.usermedia.isConnected)
  const currentUserStreamId = computed(() => store.state.usermedia.streamDocId)
  const isConnectedToCast = computed(() => !!cast.value?.source_streams[currentUserStreamId.value ?? ''])
  const { constructCasterStreamInfo } = useCasterStreamInfo()
  const { isCastTalkbackActive } = useTalkback()
  const isTalkingToCast = computed(() => isCastTalkbackActive(castId.value))

  const connectToCast = async () => {
    if (!isUserMediaConnected.value || !castId.value) return
    const streamInfo = constructCasterStreamInfo()
    if (streamInfo === null) {
      consoleError('Cannot connect to cast. Failed to construct current user stream info.', { castId: castId.value })
      return
    }
    castClientCount.increment(castId.value)
    if (castClientCount.get(castId.value) === 1) {
      await store.dispatch.events.addCasterStreamToCast({ streamInfo, castId: castId.value })
      await store.dispatch.user.addCastToPresence(castId.value)
    }
  }

  const disconnectFromCast = async (aCastId?: string) => {
    const targetCastId = aCastId ?? castId.value
    if (!currentUserStreamId.value || !targetCastId) return
    castClientCount.decrement(castId.value)
    if (castClientCount.get(castId.value) === 0) {
      await store.dispatch.events.removeCasterStreamFromCast({
        streamId: currentUserStreamId.value, castId: targetCastId
      })
      await store.dispatch.user.removeCastFromPresence(targetCastId)
    } else if (castClientCount.get(castId.value) < 0) {
      castClientCount.reset(castId.value)
    }
  }

  const startCastTalkback = async () => {
    if (isTalkingToCast.value || !castId.value) return
    const castTalkbackTarget = constructCastTalkbackTarget(castId.value)
    if (isUserMediaConnected.value && isConnectedToCast.value) {
      await store.dispatch.talkback.startTalkback(castTalkbackTarget)
    } else {
      await store.dispatch.ui.openModal(Modals.USER_MEDIA_CONNECT)
    }
  }

  const stopCastTalkback = async () => {
    if (!isTalkingToCast.value) return
    await store.dispatch.talkback.stopTalkback()
  }

  onMounted(async () => {
    if (castId.value) await connectToCast()
  })

  onUnmounted(async () => {
    if (castId.value) await disconnectFromCast()
  })

  watch(castId, async (newVal, oldVal) => {
    if (oldVal) await disconnectFromCast(oldVal)
    if (newVal) await connectToCast()
  })

  watch(isUserMediaConnected, async (newVal, oldVal) => {
    if (oldVal) await disconnectFromCast()
    if (newVal) await connectToCast()
  })

  watch(isConnectedToCast, (newVal) => {
    if (!newVal) castClientCount.reset(castId.value)
  })

  return {
    isConnectedToCast,
    isUserMediaConnected,
    startCastTalkback,
    stopCastTalkback
  }
}

export default useCastTalkback
